/**
 * URL publique d'un produit de boutique — `/boutique/{shopSlug}/{productSlug}`.
 *
 * ⚠️ DÉCISION D'ARCHITECTURE (chantier boutique, fiche produit dédiée).
 *
 * `shop_products` n'a PAS de colonne `slug` (migration 160) et ce chantier est
 * purement FRONT : hors de question d'exiger une migration + un champ à saisir
 * dans le dashboard pour publier un lien partageable. L'identifiant d'URL est
 * donc DÉRIVÉ, sans aucun aller-retour serveur :
 *
 *     `{slugify(nom)}-{8 premiers caractères de l'uuid}`
 *     ex. « T-shirt Donoor » → `t-shirt-donoor-86bec44c`
 *
 * Propriétés qui motivent ce choix :
 *  - **lisible** (le lien collé dans un groupe WhatsApp dit ce qu'il vend) ;
 *  - **jamais cassé si le nom change** : la RÉSOLUTION n'utilise QUE le
 *    suffixe d'id (`resolveShopProductFromSlug`). Un lien partagé en janvier
 *    reste valide après un renommage en mars — la partie texte est purement
 *    décorative, exactement comme les URLs Notion/Linear ;
 *  - **zéro migration, zéro collision** : l'uuid discrimine, et si deux
 *    produits d'une même boutique partageaient les 8 mêmes caractères
 *    (probabilité négligeable, et la comparaison reste bornée à UNE boutique),
 *    on retombe sur le premier trouvé — jamais sur un produit d'une autre
 *    boutique.
 *
 * Les URLs déjà partagées avec l'uuid COMPLET (ou toute variante contenant le
 * préfixe) continuent de résoudre : `resolveShopProductFromSlug` accepte aussi
 * l'id brut.
 */
import { slugify } from "@donoor/utils";
import type { ShopProduct } from "~/types/shop";

/** Nombre de caractères d'uuid conservés comme discriminant d'URL. */
const ID_SUFFIX_LENGTH = 8;

/** Suffixe d'id d'un produit (8 premiers caractères de l'uuid, minuscules). */
export function shopProductIdSuffix(productId: string): string {
  return String(productId).toLowerCase().replace(/-/g, "").slice(0, ID_SUFFIX_LENGTH);
}

/**
 * Segment d'URL d'un produit : `{nom-slugifié}-{suffixe-id}`.
 * Le nom est tronqué (60) pour garder une URL courte et partageable.
 */
export function shopProductSlug(product: ShopProduct): string {
  const namePart = slugify(product.name || "", 60);
  const idPart = shopProductIdSuffix(product.id);
  return namePart ? `${namePart}-${idPart}` : idPart;
}

/** URL complète (relative) de la fiche produit. */
export function shopProductPath(shopSlug: string, product: ShopProduct): string {
  return `/boutique/${shopSlug}/${shopProductSlug(product)}`;
}

/**
 * Retrouve le produit désigné par un segment d'URL.
 *
 * Résolution TOLÉRANTE, dans cet ordre :
 *  1. id complet (`/boutique/x/8f14e45f-ceea-...`) — liens historiques ;
 *  2. suffixe d'id en fin de segment (cas nominal, insensible au renommage) ;
 *  3. slug du nom seul (produit sans suffixe, saisi à la main).
 *
 * @returns le produit, ou `null` si aucun ne correspond.
 */
export function resolveShopProductFromSlug(
  products: ShopProduct[],
  rawSegment: string,
): ShopProduct | null {
  const segment = decodeURIComponent(String(rawSegment || "")).trim().toLowerCase();
  if (!segment) return null;

  // 1. Id complet.
  const byId = products.find((p) => String(p.id).toLowerCase() === segment);
  if (byId) return byId;

  // 2. Suffixe d'id — la partie APRÈS le dernier tiret, ou le segment entier
  //    si le produit n'a pas de nom slugifiable.
  const lastDash = segment.lastIndexOf("-");
  const candidateSuffix = lastDash >= 0 ? segment.slice(lastDash + 1) : segment;
  if (candidateSuffix) {
    const bySuffix = products.find(
      (p) => shopProductIdSuffix(p.id) === candidateSuffix,
    );
    if (bySuffix) return bySuffix;
  }

  // 3. Slug du nom seul (dernier recours — ambigu si deux produits homonymes,
  //    on prend le premier, ce qui reste préférable à un 404).
  const byName = products.find((p) => slugify(p.name || "", 60) === segment);
  return byName ?? null;
}
